import { createContext, useContext, useMemo, useState, type PropsWithChildren } from 'react'
import { CssBaseline, ThemeProvider, alpha, createTheme } from '@mui/material'

interface ColorModeValue { dark: boolean; toggle: () => void }
const ColorModeContext = createContext<ColorModeValue>({ dark: false, toggle: () => undefined })

// Paleta -- mantém o indigo/ciano da marca (mesmo gradiente da logomarca em AppShell.tsx),
// só refina os tons pra não ficar "roxo genérico de SaaS": indigo mais profundo no claro,
// cinzas neutros com leve viés azulado nas bordas/fundos em vez do cinza puro do MUI padrão.
const brand = { indigo: '#4338ca', indigoDark: '#818cf8', cyan: '#0891b2' }

export function AppTheme({ children }: PropsWithChildren) {
  const [dark, setDark] = useState(() => localStorage.getItem('emailserver.theme') === 'dark')
  const mode = dark ? 'dark' : 'light'
  const border = dark ? '#1f2937' : '#e4e7f0'
  const theme = useMemo(() => createTheme({
    palette: {
      mode,
      primary: { main: dark ? brand.indigoDark : brand.indigo },
      secondary: { main: brand.cyan },
      background: dark ? { default: '#070b14', paper: '#111827' } : { default: '#f6f7fb', paper: '#ffffff' },
      ...(!dark && { text: { primary: '#0f172a', secondary: '#5b6474' } }),
      divider: border,
    },
    typography: {
      fontFamily: '"Inter", sans-serif',
      h4: { fontWeight: 800, letterSpacing: '-0.03em' },
      h5: { fontWeight: 700, letterSpacing: '-0.02em' },
      h6: { fontWeight: 700, letterSpacing: '-0.01em' },
      subtitle2: { fontWeight: 600 },
      button: { fontWeight: 600 },
      overline: { fontWeight: 700, letterSpacing: '0.08em' },
    },
    shape: { borderRadius: 10 },
    components: {
      MuiCssBaseline: { styleOverrides: { '::selection': { backgroundColor: alpha(dark ? brand.indigoDark : brand.indigo, 0.25) } } },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: `1px solid ${border}`,
            boxShadow: dark ? '0 16px 50px rgba(0,0,0,.22)' : '0 1px 2px rgba(15,23,42,.04), 0 12px 30px rgba(15,23,42,.05)',
          },
        },
      },
      MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } }, defaultProps: { elevation: 0 } },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { textTransform: 'none', fontWeight: 600, borderRadius: 8, paddingInline: 16 },
          contained: { boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
          outlined: { borderColor: border },
        },
      },
      MuiIconButton: { styleOverrides: { root: { borderRadius: 8 } } },
      MuiTextField: { defaultProps: { size: 'small' } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 9 },
          notchedOutline: { borderColor: border },
        },
      },
      MuiInputLabel: { styleOverrides: { root: { fontWeight: 500 } } },
      MuiDialog: { styleOverrides: { paper: { backgroundImage: 'none', borderRadius: 14 } } },
      MuiDialogTitle: { styleOverrides: { root: { fontWeight: 700, fontSize: '1.15rem' } } },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600, borderRadius: 7 },
          outlined: { borderColor: border },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: border },
          head: { fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.04em', textTransform: 'uppercase', color: dark ? '#8b95a7' : '#6b7385' },
        },
      },
      MuiTableRow: { styleOverrides: { root: { '&:last-of-type td': { borderBottom: 'none' } } } },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backdropFilter: 'blur(16px)',
            backgroundColor: dark ? 'rgba(7,11,20,.72)' : 'rgba(255,255,255,.78)',
            borderBottom: `1px solid ${border}`,
          },
        },
      },
      MuiDrawer: { styleOverrides: { paper: { borderRightColor: border, backgroundImage: 'none' } } },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              backgroundColor: alpha(dark ? brand.indigoDark : brand.indigo, dark ? 0.16 : 0.09),
              '&:hover': { backgroundColor: alpha(dark ? brand.indigoDark : brand.indigo, dark ? 0.2 : 0.12) },
            },
          },
        },
      },
      MuiTooltip: { styleOverrides: { tooltip: { fontWeight: 500, fontSize: '0.72rem', borderRadius: 6 } } },
      MuiLinearProgress: { styleOverrides: { root: { backgroundColor: dark ? 'rgba(255,255,255,.08)' : 'rgba(15,23,42,.06)' } } },
      MuiDivider: { styleOverrides: { root: { borderColor: border } } },
      MuiMenu: { styleOverrides: { paper: { border: `1px solid ${border}`, boxShadow: dark ? '0 16px 50px rgba(0,0,0,.35)' : '0 12px 30px rgba(15,23,42,.1)' } } },
    },
  }), [dark, mode, border])
  const value = useMemo(() => ({ dark, toggle: () => setDark((current) => { localStorage.setItem('emailserver.theme', current ? 'light' : 'dark'); return !current }) }), [dark])
  return <ColorModeContext.Provider value={value}><ThemeProvider theme={theme}><CssBaseline />{children}</ThemeProvider></ColorModeContext.Provider>
}

export const useColorMode = () => useContext(ColorModeContext)
